import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPageSEO, fetchPublicSettings } from "@/lib/api";

/**
 * Applies SEO metadata for the given path by editing <head> tags client-side.
 * Not a substitute for SSR, but works for browsers and modern Googlebot.
 * Falls back to provided defaults when no admin record exists.
 *
 * Canonical URL preference (matches production intent):
 *   1. Explicit `canonical` set on the SEO record.
 *   2. Admin-configured `site_url` + current pathname.
 *   3. Fallback: `window.location.origin` + pathname (never query string).
 */
export default function PageSEO({ path, defaultTitle, defaultDescription, defaultImage }) {
    const { data } = useQuery({
        queryKey: ["seo", path],
        queryFn: () => fetchPageSEO(path),
        staleTime: 60 * 1000,
    });
    const { data: settings } = useQuery({
        queryKey: ["public-settings"],
        queryFn: fetchPublicSettings,
        staleTime: 5 * 60 * 1000,
    });
    const rec = data?.item || null;
    const siteUrl = (settings?.site_url || "").trim().replace(/\/+$/, "");

    useEffect(() => {
        const title = (rec?.title || defaultTitle || "").trim();
        const description = (rec?.description || defaultDescription || "").trim();
        const ogImage = (rec?.og_image || defaultImage || "").trim();
        const explicitCanonical = (rec?.canonical || "").trim();
        const noindex = !!rec?.noindex;

        // Derive a clean canonical URL — strips query strings and hash fragments
        // to avoid duplicate-content signals to search engines.
        const pathname = (path || window.location.pathname || "/").replace(/\/+$/, "") || "/";
        const base = siteUrl || window.location.origin.replace(/\/+$/, "");
        const canonicalUrl = explicitCanonical || `${base}${pathname === "/" ? "" : pathname}` || base;

        if (title) document.title = title;
        setMeta("description", description);
        setMeta("og:title", title, "property");
        setMeta("og:description", description, "property");
        if (ogImage) setMeta("og:image", ogImage, "property");
        setMeta("og:url", canonicalUrl, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", title);
        setMeta("twitter:description", description);
        if (ogImage) setMeta("twitter:image", ogImage);
        setLink("canonical", canonicalUrl);
        setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
    }, [rec, defaultTitle, defaultDescription, defaultImage, path, siteUrl]);

    return null;
}

function setMeta(name, content, attr = "name") {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href) {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}
